import { type FC, useState } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { HAS_AIS } from '@/services/ais-stream';

// ---------------------------------------------------------------------------
// Layer Controls — floating toggle panel on map (bottom-left desktop / left side-tab mobile)
// ---------------------------------------------------------------------------

interface LayerDef {
  id: string;
  label: string;
  colorDot: string;   // Tailwind bg-* class
  group: 'events' | 'intel' | 'military' | 'transport' | 'infra';
  sim?: boolean;
}

const LAYERS: LayerDef[] = [
  // Events
  { id: 'war-zones',        label: 'War Zones',       colorDot: 'bg-red-500',     group: 'events' },
  { id: 'earthquakes',      label: 'Earthquakes',     colorDot: 'bg-amber-500',   group: 'events' },
  { id: 'wildfires',        label: 'Wildfires',       colorDot: 'bg-orange-500',  group: 'events' },
  // Intel
  { id: 'missiles',         label: 'Missiles',        colorDot: 'bg-red-400',     group: 'intel', sim: true },
  { id: 'drone-attacks',    label: 'Drone Attacks',   colorDot: 'bg-yellow-400',  group: 'intel', sim: true },
  { id: 'troop-movements',  label: 'Force Movements', colorDot: 'bg-orange-400',  group: 'intel' },
  { id: 'acled',            label: 'Conflict Events', colorDot: 'bg-rose-500',    group: 'intel' },
  { id: 'cyber-threats',    label: 'Cyber Threats',   colorDot: 'bg-purple-400',  group: 'intel' },
  { id: 'cities',           label: 'World Cities',    colorDot: 'bg-yellow-200',  group: 'intel' },
  { id: 'chokepoints',      label: 'Chokepoints',     colorDot: 'bg-red-400',     group: 'intel' },
  // Military
  { id: 'fighters',         label: 'Fighter Jets',    colorDot: 'bg-pink-400',    group: 'military', sim: true },
  { id: 'naval',            label: 'Naval Fleets',    colorDot: 'bg-violet-500',  group: 'military', sim: true },
  { id: 'carriers',         label: 'Carrier Groups',  colorDot: 'bg-sky-400',     group: 'military', sim: true },
  { id: 'drone-zones',      label: 'Drone Zones',     colorDot: 'bg-orange-400',  group: 'military' },
  { id: 'nuclear',          label: 'Nuclear Sites',   colorDot: 'bg-yellow-400',  group: 'military' },
  { id: 'notam',            label: 'Flight Restrict', colorDot: 'bg-amber-400',   group: 'military' },
  // Transport
  { id: 'aircraft',         label: 'Live Aircraft',   colorDot: 'bg-cyan-400',    group: 'transport' },
  { id: 'military-aircraft',label: 'Mil Aircraft',    colorDot: 'bg-violet-400',  group: 'transport' },
  // AIS 키가 있으면 실시간이므로 SIM 배지를 떼야 한다 (가짜 표기 방지)
  { id: 'ships',            label: HAS_AIS ? 'Live Vessels (AIS)' : 'Cargo Ships', colorDot: HAS_AIS ? 'bg-emerald-300' : 'bg-sky-400', group: 'transport', sim: !HAS_AIS },
  { id: 'submarine-cables', label: 'Undersea Cables', colorDot: 'bg-indigo-400',  group: 'transport' },
  // Infrastructure
  { id: 'power-plants',      label: 'Power Plants',    colorDot: 'bg-yellow-300',  group: 'infra' },
  { id: 'night-lights',      label: 'Night Lights',    colorDot: 'bg-indigo-300',  group: 'infra' },
  { id: 'recent-satellite',  label: 'Recent Imagery',  colorDot: 'bg-emerald-300', group: 'infra' },
  { id: 'webcams',           label: 'Live Webcams',    colorDot: 'bg-cyan-400',    group: 'infra' },
  { id: 'traffic-cctv',      label: 'Traffic CCTV',    colorDot: 'bg-rose-300',   group: 'infra' },
];

const GROUP_LABELS: Record<string, string> = {
  events:    'EVENTS',
  intel:     'INTEL',
  military:  'MILITARY',
  transport: 'TRANSPORT',
  infra:     'INFRA',
};

// Shared side-tab button style
const SIDE_TAB_CLS = [
  'flex flex-col items-center justify-center gap-1',
  'h-20 w-8',
  'bg-surface-panel/95 backdrop-blur-sm border border-surface-border',
  'text-slate-300 shadow-lg shadow-black/40 cursor-pointer transition-colors hover:bg-surface-hover',
].join(' ');

export const LayerControls: FC = () => {
  const visibleLayers = useAppStore((s) => s.visibleLayers);
  const toggleLayer = useAppStore((s) => s.toggleLayer);
  const [expanded, setExpanded] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth >= 768,
  );

  const groups = ['events', 'intel', 'military', 'transport', 'infra'] as const;
  const satOn = visibleLayers.has('satellite');

  const satelliteBtn = (
    <button
      onClick={() => toggleLayer('satellite')}
      className={clsx(
        'w-full flex items-center gap-2 px-3 py-2 text-[11px] font-bold transition-colors border-b',
        satOn
          ? 'text-emerald-300 bg-emerald-950/50 border-emerald-800/60 hover:bg-emerald-900/60'
          : 'text-slate-400 border-surface-border hover:text-slate-200 hover:bg-surface-hover',
      )}
    >
      <span className="text-base leading-none">🛰</span>
      <span className="flex-1 text-left tracking-wide">Satellite</span>
      <span className={clsx('text-[8px] font-bold px-1.5 py-0.5 rounded',
        satOn ? 'bg-emerald-900/80 text-emerald-300' : 'bg-slate-800/60 text-slate-500',
      )}>{satOn ? 'ON' : 'OFF'}</span>
    </button>
  );

  const layerList = (
    <div className="py-1">
      {groups.map((group) => {
        const groupLayers = LAYERS.filter((l) => l.group === group);
        if (!groupLayers.length) return null;
        return (
          <div key={group} className="mb-1">
            <div className="px-3 py-0.5 text-[9px] font-bold uppercase tracking-widest text-slate-600">
              {GROUP_LABELS[group]}
            </div>
            {groupLayers.map((layer) => {
              const on = visibleLayers.has(layer.id);
              return (
                <button
                  key={layer.id}
                  onClick={() => toggleLayer(layer.id)}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-1 text-[10px] transition-colors',
                    on
                      ? 'text-slate-100 hover:bg-surface-hover'
                      : 'text-slate-600 hover:text-slate-400 hover:bg-surface-hover',
                  )}
                >
                  <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', layer.colorDot, on ? 'opacity-100' : 'opacity-20')} />
                  <span className="truncate flex-1 text-left">{layer.label}</span>
                  {layer.sim && (
                    <span className="text-[7px] font-bold px-1 rounded shrink-0 bg-slate-700/60 text-slate-500 mr-0.5">SIM</span>
                  )}
                  <span className={clsx('text-[8px] font-bold px-1 rounded shrink-0',
                    on ? 'bg-emerald-900/60 text-emerald-400' : 'bg-slate-800/60 text-slate-600',
                  )}>
                    {on ? 'ON' : 'OFF'}
                  </span>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {/* ── Desktop layout ─────────────────────────────────────────── */}
      <div
        className="hidden md:block absolute bottom-12 left-2 z-40 select-none"
        style={{ maxHeight: 'calc(100vh - 8rem)', overflowY: 'auto' }}
      >
        <div className="bg-surface-panel/90 backdrop-blur-md border border-surface-border rounded-lg shadow-xl shadow-black/40 overflow-hidden w-44">
          {satelliteBtn}
          {/* Layers expand/collapse toggle */}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-200 border-b border-surface-border transition-colors"
          >
            <span>Layers</span>
            <span className="text-slate-500">{expanded ? '▲' : '▼'}</span>
          </button>
          {expanded && layerList}
        </div>
      </div>

      {/* ── Mobile side-tab (left edge, vertically centered) ───────── */}
      {/* Tab button — fixed independently so expansion never shifts position */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className={clsx(
          'fixed left-0 top-1/2 -translate-y-1/2 z-40 md:hidden select-none',
          'flex flex-col items-center justify-center gap-0.5',
          'h-20 w-8 rounded-r-lg border border-l-0',
          'shadow-lg shadow-black/40 cursor-pointer transition-colors',
          expanded
            ? 'bg-slate-800/95 backdrop-blur-sm border-slate-600 text-slate-200'
            : 'bg-surface-panel/95 backdrop-blur-sm border-surface-border text-slate-300 hover:bg-surface-hover',
        )}
      >
        <span className="text-[9px] font-bold text-slate-500 tracking-wider">LAY</span>
        <span className="text-base leading-none">🗂</span>
        <span className="text-[13px] font-bold leading-none">{expanded ? '‹' : '›'}</span>
      </button>

      {/* Expanding panel — separate fixed element, no layout shift on tab */}
      {expanded && (
        <div
          className="fixed left-8 top-1/2 -translate-y-1/2 z-40 md:hidden
            bg-surface-panel/95 backdrop-blur-md border border-l-0 border-surface-border
            rounded-r-lg shadow-xl shadow-black/40 w-40 overflow-hidden select-none"
          style={{ maxHeight: 'calc(100vh - 8rem)', overflowY: 'auto' }}
        >
          {satelliteBtn}
          {layerList}
        </div>
      )}
    </>
  );
};
